import {
  mapQueryAccessFilters,
  mapQueryAccessUsers,
  queryAccessFilters,
  queryAccessUsers,
  mapQueryAccessObjectCounts,
  mapQueryAccessUserCounts,
  queryAccessObjectCounts,
  queryAccessUserCounts,
  queryAnalysisList,
  mapQueryAnalysisList,
  queryCreateNewReviewAnalysis,
  queryAnalysisDetailsById,
  mapQueryAnalysisDetailsById,
  queryAnalysisOverviewDetailsById,
  mapQueryAnalysisOverviewDetailsById,
  queryDeleteReviewAnalysis,
  queryAccessGroups,
  queryAccessAuditLogs,
  mapQueryAccessAuditLogs,
  mapQueryAccessGroups,
  queryMarkReviewAsCompleted,
  mutationChangeReviewStatus,
  queryAccessReviewObjectsList,
  mapQueryAccessReviewObjectsList,
  queryAccessReviewObjectsCount,
  mapQueryAccessReviewObjectsCount,
  mutationEditAnalysis,
  mutationReRunAnalysis,
  queryAccessAuditLogPrincipals,
  mapQueryAccessAuditLogPrincipals,
  mutationRevokeAccess,
  queryTotalAnalysis,
  mapQueryTotalAnalysis,
  AccessFiltersParams
} from './queries'

import {
  ANALYSIS_NAME,
  ANALYSIS_OWNER,
  DATA_SOURCE,
  FOLDER_PATH
} from './components/createNewReviewAnalysisBtn/createNewReviewAnalysisBtn'
import { DataSource } from '../../../services/api/apiTypes'
import graphqlService from '../../../services/graphqlService'
import { mapQueryDataSources, queryDataSources } from '../../users/queries'
import { UsersDatasourceInfo } from '../../users/usersSlice'
import {
  ACCESS_REVIEW_ALLOWED_DATA_SOURCE_TYPES,
  DATA_SOURCE_TYPES,
  GRAPHQL_API_FILTERS,
  PAGE,
  PAGE_SIZE
} from '../../../constants'
import {
  AccessControlAccessMode,
  AccessControlEmployeeType,
  AccessControlUserType
} from '../../accessControl/types'
import { DownloadListParams, FilterParams } from '../../../interfaces'

import apiService from '../../../services/api/apiService'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

export interface IAnalysisGroup {
  id: string
  name: string
  objectsCount: number
  membersCount: number
  owner: string
  reviewStatus: AccessReviewStatuses
  reviewDate?: string
  sensitiveObjectsCount: number
  totalObjectsCount: number
}
export interface IAnalysisUser {
  id: string
  name: string
  sensitiveObjectsCount: number
  totalObjectsCount: number
  totalFolderCount?: number
  department: string
  groupsCount: number
  groupsList: { id: string; name: string }[]
  employmentType: AccessControlEmployeeType
  reviewStatus: AccessReviewStatuses
  reviewDate?: string
  reviewedBy?: string
  userType?: AccessControlUserType
  accessTypes?: AccessControlAccessMode[]
}

export type UserAccessFilters = {
  departments?: string[]
  totalDepartments?: number
}
export interface IAnalysis {
  id?: string
  name?: string
  createdAt?: string
  updateTimestamp?: string
  workflowStatus?: AnalyticsWorkflowStatuses
  reviewStatus?: AnalyticsReviewStatuses
  path?: string
  webUrl?: string
  createdBy?: string
  ownerEmail?: string
  type?: string
  overview?: {
    allUsers?: number
    externalUsers?: number
    internalUsers?: number
    directAccessUsers?: number
    groupAccessUsers?: number
    unReviewedUsers?: number
    reviewedUsers?: number
    flaggedUsers?: number
    unReviewedGroups?: number
    flaggedGroups?: number
  }
  groups?: IAnalysisGroup[]
  totalGroups?: number
  auditLogs?: AnalysisAuditLog[]
  totalAuditLogs?: number
  objectsList?: AccessReviewObject[]
  totalObjects?: number
  users?: IAnalysisUser[]
  filters?: UserAccessFilters
  totalUsers?: number
  dataSource?: DataSource
}
export enum UserAccessPrincipalTypes {
  user = 'USER',
  group = 'GROUP'
}
export enum AnalyticsWorkflowStatuses {
  inProgress = 'IN_PROGRESS',
  success = 'SUCCESS',
  pending = 'PENDING',
  failed = 'FAILED'
}
export enum AnalyticsReviewStatuses {
  open = 'OPEN',
  closed = 'CLOSED',
  completed = 'COMPLETED',
  scanInProgress = 'SCAN_IN_PROGRESS',
  scanCompleted = 'SCAN_COMPLETED',
  reviewInProgress = 'REVIEW_IN_PROGRESS'
}
export enum AccessReviewStatuses {
  reviewed = 'REVIEWED',
  notReviewed = 'UNREVIEWED',
  flagged = 'FLAGGED',
  revokeInProgress = 'REVOKE_IN_PROGRESS',
  revokedBySystem = 'ACCESS_REVOKED_BY_SYSTEM',
  revokedByUser = 'ACCESS_REVOKED_BY_USER'
}

export type UserAccessObjects = {
  totalFilesCount?: number
  openAccessFilesCount?: number
  staleFilesCount?: number
}
export type UserAccessUsers = {
  totalUsersCount?: number
  externalUsersCount?: number
}
export interface ISecureNowState {
  dataSources: UsersDatasourceInfo[]
  analysisDetail: IAnalysis
  analysisList: {
    list: IAnalysis[]
    total: number
  }
  totalAnalysis?: number
  accessObjects?: UserAccessObjects
  accessUsers?: UserAccessUsers
  showLoader: boolean
  createdByList: string[]
}

const initialState: ISecureNowState = {
  dataSources: [],
  analysisDetail: {},
  analysisList: {
    list: [],
    total: 0
  },
  showLoader: false,
  createdByList: []
}

export const ACTION_ACCESS_REVIEW_DATA_SOURCES_LIST = 'accessReview/datasources/list'
export const fetchDataSources = createAsyncThunk(
  ACTION_ACCESS_REVIEW_DATA_SOURCES_LIST,
  async () => {
    const results = await graphqlService.execute(queryDataSources())
    /* For initial cut, to support only sharePoint datasource */
    return mapQueryDataSources(results)
  }
)

export interface ReviewAnalysisParams {
  [DATA_SOURCE]: string
  [FOLDER_PATH]: string
  [ANALYSIS_NAME]: string
  [ANALYSIS_OWNER]: string
  directoryId: string
  rootId: string
  flowId?: string
}

export const ACTION_CREATE_NEW_REVIEW_ANALYSIS = 'accessReview/createAnalysis'
export const createNewReviewAnalysis = createAsyncThunk(
  ACTION_CREATE_NEW_REVIEW_ANALYSIS,
  async (params: ReviewAnalysisParams, { rejectWithValue }) => {
    try {
      return await graphqlService.execute(queryCreateNewReviewAnalysis(params))
    } catch (error: any) {
      return rejectWithValue({ statusMessage: error?.errors[0]?.message || '' })
    }
  }
)

export const ACTION_RE_RUN_ANALYSIS = 'secureNow/reRunAnalysis'
export const reRunAnalysis = createAsyncThunk(ACTION_RE_RUN_ANALYSIS, async (reviewId: string) => {
  return await graphqlService.execute(mutationReRunAnalysis(reviewId))
})

export type AnalysisEditParams = {
  reviewId: string
  name: string
  ownerEmail: string
  ownerName: string
}
export const ACTION_EDIT_ANALYSIS = 'secureNow/editAnalysis'
export const editAnalysis = createAsyncThunk(
  ACTION_EDIT_ANALYSIS,
  async (params: AnalysisEditParams) => {
    return await graphqlService.execute(mutationEditAnalysis(params))
  }
)
export const ACTION_MARK_REVIEW_AS_COMPLETED = 'secureNow/markReviewAsCompleted'
export const markReviewAsCompleted = createAsyncThunk(
  ACTION_MARK_REVIEW_AS_COMPLETED,
  async (reviewId: string) => {
    await graphqlService.execute(queryMarkReviewAsCompleted(reviewId))
  }
)

export const ACTION_DELETE_REVIEW_ANALYSIS = 'accessReview/deleteAnalysis'
export const deleteReviewAnalysis = createAsyncThunk(
  ACTION_DELETE_REVIEW_ANALYSIS,
  async (reviewId: string) => {
    await graphqlService.execute(queryDeleteReviewAnalysis(reviewId))
  }
)

export interface FetchReviewAnalysisParams {
  dataSourceIds?: string[]
  analysisStatus?: string[]
  createdBy?: string
  searchText?: string
  [PAGE]: number
  [PAGE_SIZE]: number
}

export const ACTION_FETCH_REVIEW_ANALYSIS_LIST = 'accessReview/fetch/analysisList'
export const fetchReviewAnalysisList = createAsyncThunk(
  ACTION_FETCH_REVIEW_ANALYSIS_LIST,
  async (params: FetchReviewAnalysisParams) => {
    const response = await graphqlService.execute(queryAnalysisList(params))
    return mapQueryAnalysisList(response)
  }
)

export const ACTION_FETCH_REVIEW_ANALYSIS_DETAILS_BY_ID = 'accessReview/fetch/analysisDetails/id'
export const fetchReviewAnalysisDetailsById = createAsyncThunk(
  ACTION_FETCH_REVIEW_ANALYSIS_DETAILS_BY_ID,
  async (reviewId: string) => {
    const response = await graphqlService.execute(queryAnalysisDetailsById(reviewId))
    return mapQueryAnalysisDetailsById(response)
  }
)

export const ACTION_FETCH_REVIEW_ANALYSIS_TOTAL = 'accessReview/fetch/analysisTotal'
export const fetchReviewAnalysisTotal = createAsyncThunk(
  ACTION_FETCH_REVIEW_ANALYSIS_TOTAL,
  async () => {
    const response = await graphqlService.execute(queryTotalAnalysis())
    return mapQueryTotalAnalysis(response)
  }
)

export const ACTION_FETCH_REVIEW_ANALYSIS_OVERVIEW_DETAILS_BY_ID =
  'accessReview/fetch/analysisDetails/id/overview'
export const fetchReviewAnalysisOverviewDetailsById = createAsyncThunk(
  ACTION_FETCH_REVIEW_ANALYSIS_OVERVIEW_DETAILS_BY_ID,
  async (reviewId: string) => {
    const response = await graphqlService.execute(queryAnalysisOverviewDetailsById(reviewId))
    return mapQueryAnalysisOverviewDetailsById(response)
  }
)

export const ACTION_ACCESS_OBJECTS_COUNT = 'accessReview/fetch/accessObjectsCount'
export const fetchAccessObjectsCount = createAsyncThunk(
  ACTION_ACCESS_OBJECTS_COUNT,
  async (reviewId: string) => {
    const response = await graphqlService.execute(queryAccessObjectCounts(reviewId))
    return mapQueryAccessObjectCounts(response)
  }
)

export const ACTION_ACCESS_USERS_COUNT = 'accessReview/fetch/accessUsersCount'
export const fetchAccessUsersCount = createAsyncThunk(
  ACTION_ACCESS_USERS_COUNT,
  async (reviewId: string) => {
    const response = await graphqlService.execute(queryAccessUserCounts(reviewId))
    return mapQueryAccessUserCounts(response)
  }
)

export const ACTION_ACCESS_FILTERS = 'accessReview/fetch/accessFilters'
export const fetchAccessFilters = createAsyncThunk(
  ACTION_ACCESS_FILTERS,
  async (params: AccessFiltersParams) => {
    const response = await graphqlService.execute(queryAccessFilters(params))
    return mapQueryAccessFilters(response)
  }
)

export const ACTION_ACCESS_FILTERS_PAGINATED = 'accessReview/fetch/accessFiltersPaginated'
export const fetchAccessFiltersPaginated = createAsyncThunk(
  ACTION_ACCESS_FILTERS_PAGINATED,
  async (params: AccessFiltersParams) => {
    const response = await graphqlService.execute(queryAccessFilters(params))
    return mapQueryAccessFilters(response)
  }
)

export type AccessUsersParams = {
  reviewId: string
  principalType: UserAccessPrincipalTypes
  page: number
  search?: string
  filters?: FilterParams
}

export const ACTION_ACCESS_USERS = 'accessReview/fetch/accessUsers'
export const fetchAccessUsers = createAsyncThunk(
  ACTION_ACCESS_USERS,
  async (params: AccessUsersParams) => {
    const response = await graphqlService.execute(queryAccessUsers(params))
    return mapQueryAccessUsers(response)
  }
)

export type AccessReviewChangeReviewStatusParams = {
  reviewId: string
  principalIds: string[]
  reviewStatus: AccessReviewStatuses
}
export const ACTION_ACCESS_CHANGE_REVIEW_STATUS = 'accessReview/changeReviewStatus'
export const changeAccessReviewStatus = createAsyncThunk(
  ACTION_ACCESS_CHANGE_REVIEW_STATUS,
  async (params: AccessReviewChangeReviewStatusParams) => {
    return await graphqlService.execute(mutationChangeReviewStatus(params))
  }
)

export type AccessReviewRevokeParams = {
  reviewId: string
  principalIds: string[]
}
export const ACTION_ACCESS_REVOKE = 'accessReview/revoke'
export const revokeAccess = createAsyncThunk(
  ACTION_ACCESS_REVOKE,
  async (params: AccessReviewRevokeParams) => {
    return await graphqlService.execute(mutationRevokeAccess(params))
  }
)

export const ACTION_ACCESS_GROUPS = 'accessReview/fetch/accessGroups'
export const fetchAccessGroups = createAsyncThunk(
  ACTION_ACCESS_GROUPS,
  async (params: AccessUsersParams) => {
    const response = await graphqlService.execute(queryAccessGroups(params))
    return mapQueryAccessGroups(response)
  }
)

// audit logs
export enum AnalysisActionCategories {
  crud = 'CRUD',
  stateChange = 'STATE_CHANGE',
  permissionChange = 'PERMISSION_CHANGE',
  lifecycle = 'LIFECYCLE',
  workflowAction = 'WORKFLOW_ACTION',
  bulkOperation = 'BULK_OPERATION'
}

export enum AnalysisActorTypes {
  user = 'USER',
  system = 'SYSTEM',
  apiKey = 'API_KEY',
  webHook = 'WEBHOOK',
  scheduledTask = 'SCHEDULED_TASK'
}

export enum AnalysisAuditStatus {
  pending = 'PENDING',
  inProgress = 'IN_PROGRESS',
  completed = 'COMPLETED',
  failed = 'FAILED',
  cancelled = 'CANCELLED',
  partialSuccess = 'PARTIAL_SUCCESS'
}

type AnalysisAuditLogEntity = {
  id: string
  name: string
  email?: string
  type: UserAccessPrincipalTypes
}
export type AnalysisAuditLog = {
  triggeredTimestamp?: string
  entities?: AnalysisAuditLogEntity[]
  action?: AccessReviewStatuses
  status?: AnalysisAuditStatus
  triggeredBy?: string
  triggeredByType?: AnalysisActorTypes
}

export type AccessAuditLogsParams = {
  reviewId: string
  page: number
  filters: FilterParams
}
export type AccessAuditLogObjectsParams = {
  reviewId: string
  type: UserAccessPrincipalTypes
  filters: FilterParams
}
export const ACTION_ACCESS_AUDIT_LOGS = 'accessReview/fetch/auditLogs'
export const fetchAccessAuditLogs = createAsyncThunk(
  ACTION_ACCESS_AUDIT_LOGS,
  async (params: AccessAuditLogsParams) => {
    const { reviewId } = params
    const response = await graphqlService.execute(queryAccessAuditLogs(params))
    const logs = mapQueryAccessAuditLogs(response)

    const logPromises = logs.list.map(async (log) => {
      const entityIds = log.entities?.map((entity) => entity.id || '') || []
      const type = log.entities?.[0]?.type as UserAccessPrincipalTypes
      const filters = {
        filter: [{ key: GRAPHQL_API_FILTERS.id, values: entityIds }]
      }
      const principalsResponse = await graphqlService.execute(
        queryAccessAuditLogPrincipals({ reviewId, filters, type })
      )
      const principals = mapQueryAccessAuditLogPrincipals(principalsResponse)
      const principalMap = new Map(principals.map((p) => [Number(p.id), p]))
      const updatedEntities = log.entities?.map((entity) => {
        const principal = principalMap.get(Number(entity.id)) as AnalysisAuditLogEntity
        return { ...entity, ...principal }
      })
      return { ...log, entities: updatedEntities }
    })

    const updatedLogs = await Promise.all(logPromises)
    return { list: updatedLogs, total: logs.total }
  }
)

// objects
export type AccessReviewObject = {
  name: string
  size: number
  fileId: string
  objectLink: string
  extension: string
  documentId?: string
  datasourceId?: string
  datasourceType?: DATA_SOURCE_TYPES
  createdTimestamp: string
  lastModifiedTimestamp: string
}
export type AccessReviewObjectsParams = {
  page: number
  reviewId: string
  pageSize?: number
  principalIds?: string
  hasOpenAccess?: boolean
}
export const ACTION_ACCESS_REVIEW_OBJECTS = 'accessReview/objects'
export const fetchAccessReviewObjectsList = createAsyncThunk(
  ACTION_ACCESS_REVIEW_OBJECTS,
  async (params: AccessReviewObjectsParams) => {
    const response = await graphqlService.execute(queryAccessReviewObjectsList(params))
    return mapQueryAccessReviewObjectsList(response)
  }
)
export const ACTION_ACCESS_REVIEW_OBJECTS_COUNT = 'accessReview/objectsCount'
export const fetchAccessReviewObjectsCount = createAsyncThunk(
  ACTION_ACCESS_REVIEW_OBJECTS_COUNT,
  async (params: AccessReviewObjectsParams) => {
    const response = await graphqlService.execute(queryAccessReviewObjectsCount(params))
    return mapQueryAccessReviewObjectsCount(response)
  }
)

// export
export const ACTION_USER_ACCESS_CSV = 'entity/csv'
export const fetchUserAccessCSV = createAsyncThunk(
  ACTION_USER_ACCESS_CSV,
  async (params: DownloadListParams) => await apiService.downloadFile(params)
)

export const accessReviewSlice = createSlice({
  name: 'accessReview',
  initialState,
  reducers: {
    updateCreatedByList: (state, action) => {
      state.createdByList = action.payload
    },
    updateUserReviewStatus: (state, action) => {
      const { principalIds, reviewStatus, reviewedBy, reviewDate } = action.payload
      if (state.analysisDetail.users) {
        state.analysisDetail.users = state.analysisDetail.users.map((user) => {
          if (principalIds.includes(user.id)) {
            return {
              ...user,
              reviewStatus,
              reviewedBy: reviewedBy || user.reviewedBy,
              reviewDate: reviewDate || new Date().toISOString()
            }
          }
          return user
        })
      }
    },
    updateGroupReviewStatus: (state, action) => {
      const { principalIds, reviewStatus, reviewDate } = action.payload
      if (state.analysisDetail.groups) {
        state.analysisDetail.groups = state.analysisDetail.groups.map((group) => {
          if (principalIds.includes(group.id)) {
            return {
              ...group,
              reviewStatus,
              reviewDate: reviewDate || new Date().toISOString()
            }
          }
          return group
        })
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDataSources.fulfilled, (state, { payload }) => {
      state.dataSources = payload.filter(
        (ds) =>
          ACCESS_REVIEW_ALLOWED_DATA_SOURCE_TYPES.includes(ds.type as DATA_SOURCE_TYPES) &&
          !!ds.idpId
      )
    })
    builder.addCase(fetchReviewAnalysisList.fulfilled, (state, { payload }) => {
      state.analysisList.list = payload.list
      state.analysisList.total = payload.count

      // Update createdByList with unique owner emails from the analysis list
      const ownerEmails = payload.list.map((item) => item.ownerEmail).filter(Boolean) as string[]
      const uniqueOwners = [...new Set([...state.createdByList, ...ownerEmails])].sort()
      state.createdByList = uniqueOwners
    })
    builder.addCase(fetchReviewAnalysisTotal.fulfilled, (state, { payload }) => {
      state.totalAnalysis = payload
    })
    builder.addCase(fetchReviewAnalysisDetailsById.fulfilled, (state, { payload }) => {
      state.analysisDetail = { ...state.analysisDetail, ...payload }
    })
    builder.addCase(fetchReviewAnalysisOverviewDetailsById.pending, (state) => {
      state.analysisDetail.overview = initialState.analysisDetail.overview
    })
    builder.addCase(fetchReviewAnalysisOverviewDetailsById.fulfilled, (state, { payload }) => {
      state.analysisDetail.overview = payload
    })
    builder.addCase(fetchAccessObjectsCount.fulfilled, (state, { payload }) => {
      state.accessObjects = payload
    })
    builder.addCase(fetchAccessUsersCount.fulfilled, (state, { payload }) => {
      state.accessUsers = payload
    })
    builder.addCase(fetchAccessFilters.fulfilled, (state, { payload }) => {
      state.analysisDetail.filters = payload
    })
    builder.addCase(fetchAccessFiltersPaginated.fulfilled, (state, { payload }) => {
      if (state.analysisDetail.filters) {
        state.analysisDetail.filters = {
          ...state.analysisDetail.filters,
          departments: [
            ...(state.analysisDetail.filters.departments || []),
            ...(payload.departments || [])
          ],
          totalDepartments: payload.totalDepartments
        }
      } else {
        state.analysisDetail.filters = payload
      }
    })
    builder.addCase(fetchAccessUsers.pending, (state) => {
      state.analysisDetail.users = initialState.analysisDetail.users
      state.analysisDetail.totalUsers = initialState.analysisDetail.totalUsers
    })
    builder.addCase(fetchAccessUsers.fulfilled, (state, { payload }) => {
      state.analysisDetail.users = payload.list
      state.analysisDetail.totalUsers = payload.total
    })
    builder.addCase(fetchAccessGroups.pending, (state) => {
      state.analysisDetail.groups = initialState.analysisDetail.groups
      state.analysisDetail.totalGroups = initialState.analysisDetail.totalGroups
    })
    builder.addCase(fetchAccessGroups.fulfilled, (state, { payload }) => {
      state.analysisDetail.groups = payload.list
      state.analysisDetail.totalGroups = payload.total
    })
    builder.addCase(fetchAccessAuditLogs.pending, (state) => {
      state.analysisDetail.auditLogs = initialState.analysisDetail.auditLogs
      state.analysisDetail.totalAuditLogs = initialState.analysisDetail.totalAuditLogs
    })
    builder.addCase(fetchAccessAuditLogs.fulfilled, (state, { payload }) => {
      state.analysisDetail.auditLogs = payload.list
      state.analysisDetail.totalAuditLogs = payload.total
    })
    builder.addCase(fetchAccessReviewObjectsList.pending, (state) => {
      state.analysisDetail.objectsList = initialState.analysisDetail.objectsList
    })
    builder.addCase(fetchAccessReviewObjectsList.fulfilled, (state, { payload }) => {
      state.analysisDetail.objectsList = payload
    })
    builder.addCase(fetchAccessReviewObjectsCount.pending, (state) => {
      state.analysisDetail.totalObjects = initialState.analysisDetail.totalObjects
    })
    builder.addCase(fetchAccessReviewObjectsCount.fulfilled, (state, { payload }) => {
      state.analysisDetail.totalObjects = payload
    })
  }
})

export const {
  updateCreatedByList,
  updateUserReviewStatus,
  updateGroupReviewStatus
} = accessReviewSlice.actions
export default accessReviewSlice.reducer
